import { Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { APP_FEATURES, APP_ROUTES, SCREEN_MODE } from '../constants';
import Protected from '../utility/Protected';
import MainLayout from './MainLayout';
import PublicLayout from './PublicLayout';
import SessionTimeout from '../components/sessionTimeOut/SessionTimeout';
import SubscriptionGuard from '../guards/SubscriptionGuard';
import ETLGuard from '../guards/ETLGuard';
import RouteErrorBoundary from '../components/ErrorBoundary/RouteErrorBoundary';
import { clearChunkRetryFlags, lazyRetry } from '../utility/lazyRetry';
import SubscriptionFeatureGuard from '../components/SubscriptionFeatureGuard';
// Lazy load all page components for code splitting
// Public Pages (loaded together in public bundle)
const Register = lazyRetry(() => import('../pages/organizationalProfile/Register'));
const AdministrationDetails = lazyRetry(
  () => import('../pages/organizationalProfile/AdministrationDetails'),
);
const Onboarding = lazyRetry(() => import('../pages/organizationalProfile/Onboarding'));
const DemoSchedule = lazyRetry(() => import('../pages/organizationalProfile/DemoSchedule'));
const Confirmation = lazyRetry(() => import('../pages/organizationalProfile/Confirmation'));
const Login = lazyRetry(() => import('../pages').then((m) => ({ default: m.Login })));
const ForgetPassword = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ForgetPassword })),
);
const OtpSelectionForget = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.OtpSelectionForget })),
);
const NewCredential = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.NewCredential })),
);
const EnterOtp = lazyRetry(() => import('../pages').then((m) => ({ default: m.EnterOtp })));
const PasswordUpdated = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.PasswordUpdated })),
);
const InvalidTokenPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.InvalidTokenPage })),
);
const OnboardingCompletedPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.OnboardingCompletedPage })),
);
const EmailLinkPage = lazyRetry(() => import('../pages/email-link'));
const UnauthorizedPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.UnauthorizedPage })),
);
const FirstChangePassword = lazyRetry(() => import('../pages/FirstChangePassword'));
const OrganizationSelection = lazyRetry(() => import('../pages/organizationSelection'));

// Authenticated-only (role-based, NOT privilege-gated) — common role-neutral landing page
const Home = lazyRetry(() => import('../pages/Home'));

// Protected Pages - Student Management
const Student = lazyRetry(() => import('../pages').then((m) => ({ default: m.Student })));
const Profile = lazyRetry(() => import('../pages').then((m) => ({ default: m.Profile })));
const StudentManagementScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.StudentManagementScreen })),
);
const ViewEditAdminStudentScreen = lazyRetry(
  () => import('../pages/classveew-admin/student-management/view-edit-screen'),
);

// Protected Pages - Teacher Management
const Teacher = lazyRetry(() => import('../pages').then((m) => ({ default: m.Teacher })));
const AddTeacher = lazyRetry(() => import('../pages').then((m) => ({ default: m.AddTeacher })));
const TeacherManagementScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.TeacherManagementScreen })),
);
const ViewEditAdminTeacherScreen = lazyRetry(
  () => import('../pages/classveew-admin/teacher-management/view-edit-screen'),
);
const AdminNewsFeedScreen = lazyRetry(() => import('../pages/classveew-admin/news-feed'));
const TeacherAttendance = lazyRetry(() => import('../pages/teacherAttendance'));
const TeacherAssign = lazyRetry(() => import('../pages/Classroom_assign'));
const AwardManagementScreen = lazyRetry(
  () => import('../pages/classveew-admin/award-management'),
);

// Protected Pages - Attendance & Time
const Attendance = lazyRetry(() => import('../pages').then((m) => ({ default: m.Attendance })));
const AttendanceCutoffTime = lazyRetry(() => import('../pages/timeSetting'));

// Protected Pages - Events
const Events = lazyRetry(() => import('../pages/events'));
const AddEditEvent = lazyRetry(() => import('../pages/addEditEvent/AddEditEvent'));
const ViewEvents = lazyRetry(() => import('../pages/viewEvents'));

// Protected Pages - Fee Management
const FeeTemplate = lazyRetry(() => import('../pages/feeTemplate'));
const StudentFees = lazyRetry(() => import('../pages/studentFees'));
const SessionHistory = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.SessionHistory })),
);

// Protected Pages - Book Management
const BookManagement = lazyRetry(() => import('../pages/libraryManagement'));
const BookCirculation = lazyRetry(() => import('../pages/bookCirculation'));

// Protected Pages - Classroom & Grade
const ClassRoom = lazyRetry(() => import('../pages').then((m) => ({ default: m.ClassRoom })));
const Grade = lazyRetry(() => import('../pages').then((m) => ({ default: m.Grade })));

// Protected Pages - Academic Management
const SyllabusManagement = lazyRetry(() => import('../pages/syllabus'));
const CreateSyllabusForm = lazyRetry(() => import('../pages/syllabus/newSyllabus'));
const CurriculumScreen = lazyRetry(() =>
  import('../pages/curriculumManagement').then((m) => ({ default: m.CurriculumScreen })),
);
const AcademicYearPage = lazyRetry(() =>
  import('../pages/academicYear').then((m) => ({ default: m.AcademicYearScreen })),
);
const Subjects = lazyRetry(() => import('../pages').then((m) => ({ default: m.Subjects })));
const SubjectAssign = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.SubjectAssign })),
);

// Protected Pages - Exam & Score
const ScoreCardPage = lazyRetry(() => import('../pages/scoreCard'));
const ViewExamSubjects = lazyRetry(() => import('../pages/viewExam'));
const HomeworkDashboard = lazyRetry(() => import('../pages/homework'));
const CreateHomework = lazyRetry(() => import('../pages/homework/CreateHomework'));
const EditHomework = lazyRetry(() => import('../pages/homework/EditHomework'));
const ViewHomework = lazyRetry(() => import('../pages/homework/ViewHomework'));
const GradingSubmissions = lazyRetry(
  () => import('../pages/homework/GradingSubmissions'),
);

// Protected Pages - Timetable
const TimeTable = lazyRetry(() => import('../pages/timeTable'));
const SetTimeTable = lazyRetry(() => import('../pages').then((m) => ({ default: m.SetTimeTable })));

// Protected Pages - User & Role Management
const UserPolicy = lazyRetry(() => import('../pages/userPolicies'));
const RoleManager = lazyRetry(() => import('../pages/addUserType'));
const AssignUserRole = lazyRetry(() => import('../pages/assignUserRole'));
const GuardianManagementPage = lazyRetry(() => import('../pages/guardianManagement'));
const OrganizationAdminManagementPage = lazyRetry(
  () => import('../pages/organizationAdminManagement'),
);

// Protected Pages - Organization Management
const ViewAllOrganizationsScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ViewAllOrganizationsScreen })),
);
const RequestOrganizationScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.RequestOrganizationScreen })),
);
const CreateOrganizationScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.CreateOrganizationScreen })),
);
const ManageOrganizationScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ManageOrganizationScreen })),
);
const InstituteDetails = lazyRetry(() => import('../pages/organizationalProfile/institue'));

// Protected Pages - Other
const Dashboard = lazyRetry(() => import('../pages').then((m) => ({ default: m.Dashboard })));
const ETLDashboard = lazyRetry(() => import('../pages/etl-dashboard'));
const EtlQuickSetup = lazyRetry(() => import('../pages/etl-quick-setup'));
const EtlQuickSetupReview = lazyRetry(() => import('../pages/etl-quick-setup/review'));
const ChangePassword = lazyRetry(() => import('../pages/changePassword'));
const ReEnrollmentManagement = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ReEnrollmentManagement })),
);
const ReEnrollmentStudentList = lazyRetry(
  () => import('../pages/reEnrollmentManagement/ReEnrollmentStudentList'),
);
const BillingAndPlans = lazyRetry(() => import('../pages/billing'));
const ForbiddenPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ForbiddenPage })),
);
const NotFoundPage = lazyRetry(() => import('../pages').then((m) => ({ default: m.NotFoundPage })));

// Protected Pages - Reports
const Reports = lazyRetry(() => import('../pages').then((m) => ({ default: m.Reports })));
const FinanceReport = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.FinanceReport })),
);
const StudentReport = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.StudentReport })),
);
const TeacherReport = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.TeacherReport })),
);
const StudentAttendanceReport = lazyRetry(() =>
  import('../pages/reports/StudentAttendanceReport').then((m) => ({ default: m.default })),
);

// Guards (direct import - not lazy loaded since they're wrappers)
import SessionGuard from '../guards/SessionGuard';

// Suspense wrapper component for lazy-loaded routes
import SuspenseLoader from '../components/Loading/SuspenseLoader';
const ClassveewAIPage = lazyRetry(() => import('../pages/classveew-ai'));

const ReadyNotifier: React.FC<{ onReady: () => void; children: React.ReactNode }> = ({
  onReady,
  children,
}) => {
  useEffect(() => {
    onReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
};

// Suspense wrapper component for lazy-loaded routes
// Shows the SuspenseLoader fallback while a lazy route loads, and keeps
// an overlay visible for at least REACT_APP_SUSPENSE_MIN_MS milliseconds
// so the animation can be observed during fast loads in development.
const LazyRoute = ({ children }: { children: React.ReactNode }) => {
  const minMs = Number(1000);
  const [ready, setReady] = useState(false);
  const [minElapsed, setMinElapsed] = useState(minMs === 0);

  useEffect(() => {
    if (minMs <= 0) return;
    const t = setTimeout(() => setMinElapsed(true), minMs);
    return () => clearTimeout(t);
  }, [minMs]);

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <ReadyNotifier onReady={() => setReady(true)}>{children}</ReadyNotifier>
      {(!ready || !minElapsed) && <SuspenseLoader />}
    </Suspense>
  );
};

export default function AppRouter() {
  // Clear chunk retry flags on successful app load
  useEffect(() => {
    clearChunkRetryFlags();
  }, []);

  const router = createBrowserRouter([
    {
      element: <PublicLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { path: '/', element: <Navigate to="/login" replace /> },
        {
          path: '/register',
          element: (
            <LazyRoute>
              <Register />
            </LazyRoute>
          ),
        },
        {
          path: '/adminstrationDetails',
          element: (
            <LazyRoute>
              <AdministrationDetails />
            </LazyRoute>
          ),
        },
        {
          path: '/onboarding',
          element: (
            <LazyRoute>
              <Onboarding />
            </LazyRoute>
          ),
        },
        {
          path: '/demoSchedule',
          element: (
            <LazyRoute>
              <DemoSchedule />
            </LazyRoute>
          ),
        },
        {
          path: '/confirmation',
          element: (
            <LazyRoute>
              <Confirmation />
            </LazyRoute>
          ),
        },
        {
          path: '/login',
          element: (
            <LazyRoute>
              <Login />
            </LazyRoute>
          ),
        },
        {
          path: '/forgetpassword',
          element: (
            <LazyRoute>
              <ForgetPassword />
            </LazyRoute>
          ),
        },
        {
          path: '/OtpForgetpassword',
          element: (
            <LazyRoute>
              <OtpSelectionForget />
            </LazyRoute>
          ),
        },
        {
          path: '/NewCredentials',
          element: (
            <LazyRoute>
              <NewCredential />
            </LazyRoute>
          ),
        },
        {
          path: '/confirmOtp',
          element: (
            <LazyRoute>
              <EnterOtp />
            </LazyRoute>
          ),
        },
        {
          path: '/PasswordUpdated',
          element: (
            <LazyRoute>
              <PasswordUpdated />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORG_INVALID_TOKEN,
          element: (
            <LazyRoute>
              <InvalidTokenPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORG_ALREADY_CREATED,
          element: (
            <LazyRoute>
              <OnboardingCompletedPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.UNAUTHORIZED,
          element: (
            <LazyRoute>
              <UnauthorizedPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.FIRST_CHANGE_PASSWORD,
          element: (
            <LazyRoute>
              <FirstChangePassword />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORGANIZATION_SELECTION,
          element: (
            <LazyRoute>
              <OrganizationSelection />
            </LazyRoute>
          ),
        },
        {
          path: '/email-link',
          element: (
            <LazyRoute>
              <EmailLinkPage />
            </LazyRoute>
          ),
        },
      ],
    },
    {
      element: (
        <ETLGuard>
          <SubscriptionGuard>
            <SessionTimeout>
              <MainLayout />
            </SessionTimeout>
          </SubscriptionGuard>
        </ETLGuard>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: APP_ROUTES.HOME,
          element: (
            <LazyRoute>
              <Protected>
                <Home />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ETL_DASHBOARD,
          element: (
            <LazyRoute>
              <Protected>
                <ETLDashboard />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ETL_QUICK_SETUP,
          element: (
            <LazyRoute>
              <Protected>
                <EtlQuickSetup />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ETL_QUICK_SETUP_REVIEW,
          element: (
            <LazyRoute>
              <Protected>
                <EtlQuickSetupReview />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CHANGE_PASSWORD,
          element: (
            <LazyRoute>
              <Protected>
                <ChangePassword />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CLASSVEEW_AI,
          element: (
            <LazyRoute>
              <ClassveewAIPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.STUDENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_MANAGEMENT]}>
                <Student />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.STUDENT}/:id/edit`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_MANAGEMENT]}>
                <Profile mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.STUDENT}/:id/view`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_MANAGEMENT]}>
                <Profile mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.EVENT_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <Events />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_EVENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <AddEditEvent />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EVENT_MANAGEMENT}/:eventId/:mode`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <AddEditEvent />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.VIEW_EVENT_CONSENT}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <ViewEvents />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.FEE_TEMPLATE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.FEE_TEMPLATE_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="fee_management">
                  <FeeTemplate />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.BILLING_AND_PLANS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.BILLING_MANAGEMENT]}>
                <BillingAndPlans />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.TEACHER_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_MANAGEMENT]}>
                <Teacher />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_TEACHER,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_MANAGEMENT]}>
                <AddTeacher />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EDIT_TEACHER}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_MANAGEMENT]}>
                <AddTeacher />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.INVOICE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.INVOICE_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="fee_management">
                  <SessionGuard>
                    <StudentFees />
                  </SessionGuard>
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SESSION_HISTORY,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.VIEW_SESSION_HISTORY]}>
                <SubscriptionFeatureGuard featureCode="fee_management">
                  <SessionHistory />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: '/attendance',
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ATTENDANCE_MARKING]}>
                <Attendance />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.RE_ENROLLMENT_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.RE_ENROLLMENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_reenrolment">
                  <ReEnrollmentManagement />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.RE_ENROLLMENT_STUDENT_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.RE_ENROLLMENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_reenrolment">
                  <ReEnrollmentStudentList />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.OPERATIONAL_TIME_SETTINGS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ATTENDANCE_CUTOFF_MANAGEMENT]}>
                <AttendanceCutoffTime />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.BOOK_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.BOOK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="library_management">
                  <BookManagement />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.BOOK_CIRCULATION,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.BOOK_CIRCULATION]}>
                <SubscriptionFeatureGuard featureCode="library_management">
                  <BookCirculation />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CLASSROOM_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSROOM_MANAGEMENT]}>
                <ClassRoom />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.MY_INSTITUTE,
          element: (
            <LazyRoute>
              <Protected>
                <InstituteDetails />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.GRADE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.GRADE_MANAGEMENT]}>
                <Grade />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ASSIGN_CLASSROOMS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_ASSIGNMENT_MANAGEMENT]}>
                <TeacherAssign />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SYLLABUS_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SYLLABUS_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_syllabus">
                  <SyllabusManagement />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ROLE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <UserPolicy />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_ROLE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <RoleManager mode={SCREEN_MODE.CREATE} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EDIT_ROLE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <RoleManager mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.VIEW_ROLE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <RoleManager mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.USER_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.USER_MANAGEMENT]}>
                <AssignUserRole />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.EXAM_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EXAM_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <ScoreCardPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.VIEW_EXAM}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EXAM_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <ViewExamSubjects />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.HOMEWORK_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <HomeworkDashboard />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <CreateHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.EDIT_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <EditHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.VIEW_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <ViewHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.HOMEWORK_TASK_GRADING,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <GradingSubmissions />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SUBJECT_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SUBJECT_MANAGEMENT]}>
                <Subjects />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: '/dashboard',
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.VIEW_DASHBOARD]}>
                <Dashboard />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.MANAGE_TIMETABLE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROSTER_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_timetable">
                  <SetTimeTable />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.TIMETABLE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROSTER_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_timetable">
                  <TimeTable />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CURRICULUM_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected>
                <CurriculumScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ACADEMIC_YEAR_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ACADEMIC_YEAR_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="academic_year">
                  <AcademicYearPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_SYLLABUS,
          element: (
            <LazyRoute>
              <Protected>
                <SubscriptionFeatureGuard featureCode="can_manage_syllabus">
                  <CreateSyllabusForm />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EDIT_SYLLABUS}/:id`,
          element: (
            <LazyRoute>
              <Protected>
                <SubscriptionFeatureGuard featureCode="can_manage_syllabus">
                  <CreateSyllabusForm />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.TEACHER_ATTENDANCE,
          element: (
            <LazyRoute>
              <Protected>
                <SubscriptionFeatureGuard featureCode="has_teacher_attendance">
                  <TeacherAttendance />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.GUARDIAN_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.USER_MANAGEMENT]}>
                <GuardianManagementPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <Reports />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_FINANCE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.FINANCE_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <FinanceReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_STUDENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <StudentReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_TEACHER,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <TeacherReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_STUDENT_ATTENDANCE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_ATTENDANCE_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <StudentAttendanceReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORGANIZATION_ADMIN_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.USER_MANAGEMENT]}>
                <OrganizationAdminManagementPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATIONS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <ViewAllOrganizationsScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_REQUEST_ORGANIZATIONS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <RequestOrganizationScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATION_CREATE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <CreateOrganizationScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATION_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <ManageOrganizationScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATION_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <ManageOrganizationScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_STUDENTS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT]}>
                <StudentManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_STUDENT_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT]}>
                <ViewEditAdminStudentScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_STUDENT_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT]}>
                <ViewEditAdminStudentScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_TEACHERS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT]}>
                <TeacherManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_TEACHER_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT]}>
                <ViewEditAdminTeacherScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_TEACHER_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT]}>
                <ViewEditAdminTeacherScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
           path: APP_ROUTES.ADMIN_NEWS_FEED,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_POST_MANAGEMENT]}>
                <AdminNewsFeedScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_AWARDS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_AWARD_MANAGEMENT]}>
                <AwardManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.FORBIDDEN,
          element: (
            <LazyRoute>
              <ForbiddenPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.WILD_CARD,
          element: (
            <LazyRoute>
              <NotFoundPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SUBJECT_ASSIGN,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SUBJECT_MANAGEMENT]}>
                <SubjectAssign />
              </Protected>
            </LazyRoute>
          ),
        },
      ],
    },
  ]);
  return <RouterProvider router={router} />;
}
